/* eslint-disable react-hooks/exhaustive-deps */
import {
  GatewayAdresse,
  GatewayContact,
  GatewayEntreprise,
  GatewayInfoSocieteOrias,
} from "@conformite/gateway";
import {
  Dropdown,
  FilterSearch,
  InfoCard,
  InputSearch,
  Link,
  ListTemplate,
  Pagination,
  Table,
  TitleWithNumber,
  useDialog,
  SidePane,
  FileInputButton,
  Button,
  FormField,
  FormRow,
  UIInputNumber,
} from "@endya-conformite/ui-kit";
import Pencil from "@endya-conformite/ui-kit/src/assets/icons/pencil.svg?react";
import PersonCancel from "@endya-conformite/ui-kit/src/assets/icons/person_cancel.svg?react";
import PersonCheck from "@endya-conformite/ui-kit/src/assets/icons/person_check.svg?react";
import Trash from "@endya-conformite/ui-kit/src/assets/icons/trash.svg?react";
import { AdhesionDataEntreprisesItem } from "@src/adhesion/AdhesionData/adhesionDataEntreprises";
import { ImportSocietesItem } from "@src/adhesion/Steps/AdhesionModeImport/useImportSocietes";
import { ParcoursAction } from "@src/parcours/NavigationButtons/ParcoursAction";
import { SaveAndQuitButton } from "@src/parcours/NavigationButtons/SaveAndQuitButton";
import { ParcoursFullPageTemplate } from "@src/parcours/Template/ParcoursFullPageTemplate";
import { isCategorieAccessoire } from "@src/payment/utils/createIntentionPaiement";
import {
  ParcoursRenouvellementStepComponentProps,
  RenouvellementStepEnum,
} from "@src/renouvellement/ParcoursRenouvellement.definition";
import { contactMediationValidationSchema } from "@src/societes/form/EntrepriseContactMediationForm";
import { ParcoursEntreprise } from "@src/societes/parcours/ParcoursEntreprise";
import { cloneDeep, omit, partition } from "lodash";
import { ChangeEvent, useMemo, useState } from "react";
import { RenouvellementEntrepriseAControllerData } from "../../RenouvellementData";
import { useImportRenouvellement } from "../introduction/useImportRenouvellement";
import styles from "./RenouvellementMajSocietesList.module.scss";
import {
  SOCIETES_PAGE_SIZE,
  useRenouvellementSocieteFiltered,
} from "./useRenouvellementSocieteFilter";

type UncontrolledCAInputNumberProps = {
  defaultValue: number | null;
  name: string;
  showError: boolean;
  onChange: (nextValue: number | null) => void;
  className?: string;
};
function UncontrolledCAInputNumber({
  defaultValue,
  name,
  showError,
  onChange,
  className,
}: UncontrolledCAInputNumberProps) {
  const [value, setValue] = useState(defaultValue);

  const valueHasChanged = defaultValue !== value;
  const valueIsValid = value !== null && value >= 0;
  return (
    <UIInputNumber
      value={value}
      className={className}
      name={name}
      hasError={(showError || valueHasChanged) && !valueIsValid}
      onChange={(e: ChangeEvent<HTMLInputElement>) => {
        const nextValue = e.target.value === "" ? null : Number(e.target.value);
        setValue(nextValue);
        onChange(nextValue);
      }}
    />
  );
}

export function transformAdhesionDataEntreprisesItemToRenouvellementEntrepriseAController(
  societe: AdhesionDataEntreprisesItem
) {
  const [categorieAccessoire, categorieNonAccessoire] = partition(
    societe.categoriesOrias || [],
    (categorie) =>
      isCategorieAccessoire(categorie, {
        coaAccessoire: !!societe.coaAccessoire,
        miaAccessoire: !!societe.miaAccessoire,
        cobspAccessoire: !!societe.cobspAccessoire,
        miobspAccessoire: !!societe.miobspAccessoire,
      })
  );
  return {
    siren: societe.siren as string,
    raisonSociale: societe.raisonSociale as string,
    adresse: {
      codePostal: societe.codePostal as string,
      libelleVoie: societe.libelleVoie as string,
      numeroVoie: societe.numeroVoie as string,
      pays: societe.pays as GatewayAdresse.Pays,
      typeVoie: societe.typeVoie as GatewayAdresse.TypeVoie,
      ville: societe.ville as string,
      complementAdresse: societe.complementAdresse,
    },
    categorieRepresentation:
      societe.categorieRepresentation as GatewayInfoSocieteOrias.CategorieRepresentation,
    categoriesActivites: categorieNonAccessoire,
    categoriesActivitesAccessoires: categorieAccessoire,
    formeJuridique: societe.formeJuridique as GatewayEntreprise.FormeJuridique,
    numeroOrias: societe.numeroOrias,
    organisationProfessionnelle:
      societe.organisationPro as GatewayInfoSocieteOrias.OrganisationProfessionnelle[],
    sousCategorieCOBSP:
      societe.sousCategorieCobsp as GatewayInfoSocieteOrias.ActiviteBancaire[],
    sousCategoriesIOBSP:
      societe.sousCategorieMiobsp as GatewayInfoSocieteOrias.ActiviteBancaire[],
    trancheEffectif:
      societe.trancheEffectif as GatewayEntreprise.TrancheEffectif,
    chiffreAffaire: societe.chiffreAffaire,
    consentementEDI: societe.consentementEDI ?? false,
    nomMediation: societe.nomMediation as string,
    prenomMediation: societe.prenomMediation as string,
    telephoneMediation: societe.telephoneMediation,
    mailMediation: societe.mailMediation as string,
    civiliteMediation:
      societe.civiliteMediation as GatewayContact.ContactCivilite,
  };
}

export function transformRenouvellementEntrepriseAControllerToAdhesionDataEntreprisesItem(
  entrepriseAControllerData?: RenouvellementEntrepriseAControllerData
): AdhesionDataEntreprisesItem | undefined {
  if (!entrepriseAControllerData) return undefined;
  return {
    categorieRepresentation: entrepriseAControllerData.categorieRepresentation,
    categoriesOrias: [
      ...entrepriseAControllerData.categoriesActivites,
      ...entrepriseAControllerData.categoriesActivitesAccessoires,
    ],
    coaAccessoire:
      entrepriseAControllerData.categoriesActivitesAccessoires.includes(
        GatewayInfoSocieteOrias.Categorie.COA
      ),
    miaAccessoire:
      entrepriseAControllerData.categoriesActivitesAccessoires.includes(
        GatewayInfoSocieteOrias.Categorie.MIA
      ),
    cobspAccessoire:
      entrepriseAControllerData.categoriesActivitesAccessoires.includes(
        GatewayInfoSocieteOrias.Categorie.COBSP
      ),
    miobspAccessoire:
      entrepriseAControllerData.categoriesActivitesAccessoires.includes(
        GatewayInfoSocieteOrias.Categorie.MIOBSP
      ),
    chiffreAffaire: entrepriseAControllerData.chiffreAffaire,
    codePostal: entrepriseAControllerData.adresse.codePostal,
    complementAdresse: entrepriseAControllerData.adresse.complementAdresse,
    libelleVoie: entrepriseAControllerData.adresse.libelleVoie,
    numeroVoie: entrepriseAControllerData.adresse.numeroVoie,
    pays: entrepriseAControllerData.adresse.pays,
    typeVoie: entrepriseAControllerData.adresse.typeVoie,
    ville: entrepriseAControllerData.adresse.ville,
    formeJuridique: entrepriseAControllerData.formeJuridique,
    numeroOrias: entrepriseAControllerData.numeroOrias,
    organisationPro: entrepriseAControllerData.organisationProfessionnelle,
    raisonSociale: entrepriseAControllerData.raisonSociale,
    siren: entrepriseAControllerData.siren,
    sousCategorieCobsp: entrepriseAControllerData.sousCategorieCOBSP,
    sousCategorieMiobsp: entrepriseAControllerData.sousCategoriesIOBSP,
    trancheEffectif: entrepriseAControllerData.trancheEffectif,
    consentementEDI: entrepriseAControllerData.consentementEDI,
    civiliteMediation: entrepriseAControllerData.civiliteMediation,
    nomMediation: entrepriseAControllerData.nomMediation,
    prenomMediation: entrepriseAControllerData.prenomMediation,
    telephoneMediation: entrepriseAControllerData.telephoneMediation,
    mailMediation: entrepriseAControllerData.mailMediation,
  };
}

export function transformImportSocietesDataToRenouvellementData(
  entreprise: ImportSocietesItem[]
): Record<string, RenouvellementEntrepriseAControllerData> {
  return entreprise.reduce(
    (acc, { societe }) => {
      acc[societe.siren as string] =
        transformAdhesionDataEntreprisesItemToRenouvellementEntrepriseAController(
          societe
        );
      return acc;
    },
    {} as Record<string, RenouvellementEntrepriseAControllerData>
  );
}

type PaneState = {
  open: boolean;
  entrepriseToEdit?: RenouvellementEntrepriseAControllerData;
};

export function RenouvellementMajSocietesList({
  data,
  setData,
  addData,
  changeStep,
  handleQuit,
}: ParcoursRenouvellementStepComponentProps) {
  const [unmountPaneAfterDeleteCount, setUnmountPaneAfterDeleteCount] =
    useState(0);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [paneState, setPaneState] = useState<PaneState>({
    open: false,
  });
  const entreprisesArray = useMemo(
    () => Object.values(data.entreprisesAControler),
    [data.entreprisesAControler]
  );
  const {
    societes,
    societesPaginated,
    numberOfSocietes,
    page,
    search,
    setPage,
    setSearch,
  } = useRenouvellementSocieteFiltered(entreprisesArray);

  const memoizedSocietesPaginated = useMemo(() => {
    return cloneDeep(societesPaginated);
  }, [data.entreprisesUpdateCount, page, search]);

  const hasMediationContact = (
    entreprise: RenouvellementEntrepriseAControllerData
  ) => {
    const result = contactMediationValidationSchema.safeParse({
      ...entreprise,
    });
    return result.success;
  };

  const societesAreValid = societes.every(
    (s) =>
      s.chiffreAffaire !== undefined &&
      s.chiffreAffaire >= 0 &&
      hasMediationContact(s)
  );

  const { handleImportSocietes } = useImportRenouvellement(
    data,
    addData,
    changeStep
  );
  const { openConfirmDialog } = useDialog();

  return (
    <>
      <ParcoursFullPageTemplate
        title={
          <TitleWithNumber
            title="Sociétés à contrôler"
            number={numberOfSocietes}
          />
        }
        actions={
          <>
            <SaveAndQuitButton onClick={handleQuit} />
            <ParcoursAction
              onClick={() => {
                setHasSubmitted(true);
                if (societesAreValid) {
                  changeStep(RenouvellementStepEnum.PAIEMENT);
                }
              }}
            >
              Suivant
            </ParcoursAction>
          </>
        }
        size="large"
      >
        {hasSubmitted && !societesAreValid && (
          <FormRow>
            <FormField>
              <InfoCard type="error" className={styles.importError}>
                Certains chiffres d&apos;affaires ou contact de médiation sont
                manquants ou invalides.
              </InfoCard>
            </FormField>
          </FormRow>
        )}
        <FormRow>
          <FormField>
            <InfoCard>
              Vous pouvez mettre à jour votre adhésion (catégories adhésion,
              pour les IOBSP vos opérations de banque, organisations
              professionnelles, …) en cliquant sur le stylet à droite du Chiffre
              d&apos;affaires.
            </InfoCard>
          </FormField>
        </FormRow>
        <FormRow>
          <FormField>
            <ListTemplate
              header={
                <FilterSearch
                  search={
                    <InputSearch
                      placeholder="Raison sociale"
                      name="search"
                      value={search}
                      onChange={(nextValue) => {
                        setSearch(nextValue.target.value);
                        setPage(0);
                      }}
                    />
                  }
                  action={
                    <div className={styles.actions}>
                      <Button
                        variant="outline"
                        onClick={() =>
                          setPaneState({
                            open: true,
                          })
                        }
                      >
                        Ajouter un entreprise
                      </Button>
                      <Dropdown content="Actions" placement="bottom-end">
                        {({ close: closeDropDown }) => (
                          <>
                            <Link to="/modele_societe.xlsx" target="_blank">
                              <Button
                                variant="text"
                                className={styles.download}
                                onClick={() => closeDropDown()}
                              >
                                Télécharger le fichier modèle
                              </Button>
                            </Link>
                            <FileInputButton
                              variant="text"
                              onChange={(e) => {
                                closeDropDown();
                                handleImportSocietes(e);
                              }}
                              accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            >
                              Importer la liste des sociétés à jour
                            </FileInputButton>
                          </>
                        )}
                      </Dropdown>
                    </div>
                  }
                />
              }
              table={
                <Table
                  data={memoizedSocietesPaginated}
                  columns={[
                    {
                      key: "mediation",
                      // title: "Contact médiation",
                      size: "auto",
                      render: (entreprise) => (
                        <div className={styles.mediationStatus}>
                          {hasMediationContact(entreprise) ? (
                            <PersonCheck className={styles.checkIcon} />
                          ) : (
                            <PersonCancel className={styles.errorIcon} />
                          )}
                        </div>
                      ),
                    },
                    {
                      key: "raisonSociale",
                      title: "Raison sociale",
                      size: 2,
                      render: (entreprise) => <p>{entreprise.raisonSociale}</p>,
                    },
                    {
                      key: "siren",
                      title: "SIREN",
                      size: 1,
                      render: (entreprise) => <p>{entreprise.siren}</p>,
                    },
                    {
                      key: "numeroOrias",
                      title: "N° Orias",
                      size: 1,
                      render: (entreprise) => <p>{entreprise.numeroOrias}</p>,
                    },
                    {
                      key: "chiffreAffaire",
                      title: "Chiffre d'affaires",
                      size: 1,
                      render: (entreprise) => (
                        <UncontrolledCAInputNumber
                          className={styles.caInput}
                          name={`ca-${entreprise.siren}`}
                          showError={hasSubmitted}
                          defaultValue={entreprise.chiffreAffaire ?? null}
                          onChange={(nextValue) => {
                            setData({
                              ...data,
                              entreprisesAControler: {
                                ...data.entreprisesAControler,
                                [entreprise.siren]: {
                                  ...entreprise,
                                  chiffreAffaire: nextValue ?? undefined,
                                },
                              },
                            });
                          }}
                        />
                      ),
                    },
                    {
                      key: "actions",
                      size: "auto",
                      render: ({ siren }) => (
                        <div className={styles.actions}>
                          <Button
                            className={styles.editButton}
                            icon={<Pencil />}
                            onClick={() => {
                              setPaneState({
                                open: true,
                                entrepriseToEdit:
                                  data.entreprisesAControler[siren], // on ne prend pas les données de la table car elles peuvent être non à jour (siren modifié directement dans le tableau)
                              });
                            }}
                            variant="text"
                          />
                          <Button
                            className={styles.deleteButton}
                            icon={<Trash />}
                            variant="text"
                            onClick={() => {
                              openConfirmDialog({
                                title: "Supprimer l'entreprise",
                                message:
                                  "Êtes-vous sur(e) de vouloir supprimer cette société ?",
                                confirmText: "Confirmer",
                                cancelText: "Annuler",
                              })
                                .then((shouldContinue) => {
                                  if (shouldContinue) {
                                    addData({
                                      entreprisesUpdateCount:
                                        data.entreprisesUpdateCount + 1,
                                      entreprisesAControler: omit(
                                        data.entreprisesAControler,
                                        siren
                                      ),
                                    });
                                    if (
                                      paneState.entrepriseToEdit?.siren ===
                                      siren
                                    )
                                      setUnmountPaneAfterDeleteCount(
                                        unmountPaneAfterDeleteCount + 1
                                      );
                                    setPaneState({
                                      open: false,
                                    });
                                  }
                                })
                                .catch(console.error);
                            }}
                          />
                        </div>
                      ),
                    },
                  ]}
                />
              }
              footer={
                <Pagination
                  page={page}
                  pageSize={SOCIETES_PAGE_SIZE}
                  total={societes.length}
                  onChange={setPage}
                />
              }
            />
          </FormField>
        </FormRow>
      </ParcoursFullPageTemplate>
      <SidePane
        key={unmountPaneAfterDeleteCount}
        isOpen={paneState.open}
        setIsOpen={(nextIsOpen) => {
          setPaneState((prevState) => ({ ...prevState, open: nextIsOpen }));
        }}
      >
        {(closePane) => (
          <ParcoursEntreprise
            key={`${JSON.stringify(paneState.entrepriseToEdit)}${
              data.entreprisesUpdateCount
            }`}
            entrepriseToEdit={transformRenouvellementEntrepriseAControllerToAdhesionDataEntreprisesItem(
              paneState.entrepriseToEdit
            )}
            withPayment={false}
            onSubmit={(adhesion: AdhesionDataEntreprisesItem) => {
              addData({
                entreprisesUpdateCount: data.entreprisesUpdateCount + 1,
                entreprisesAControler: {
                  ...data.entreprisesAControler,
                  [adhesion.siren as string]:
                    transformAdhesionDataEntreprisesItemToRenouvellementEntrepriseAController(
                      adhesion
                    ),
                },
              });
              setPaneState({ open: false });
              closePane(true);
            }}
          />
        )}
      </SidePane>
    </>
  );
}
